import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { unifiedApiService } from '../../services/unified';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { ResumeUpload, ResumeVersionHistory } from '../../components/resume';
import type { Resume } from '../../types/resume';

export function ResumePage() {
  const { isAuthenticated } = useAuth();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadResumes();
    }
  }, [isAuthenticated]);

  const loadResumes = async () => {
    try {
      setLoading(true);
      setError(null);
      const userResumes = await unifiedApiService.resume.getUserResumes();
      setResumes(userResumes.sort((a, b) => new Date(b.submitted_at || '').getTime() - new Date(a.submitted_at || '').getTime()) as any);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = async () => {
    setShowUpload(false);
    // Reload resumes to get the new one
    await loadResumes();
  };

  const handleUploadError = (error: string) => {
    setError(error);
  };

  const handleResumeUpdate = (updatedResume: Resume) => {
    setResumes(prev => 
      prev.map(resume => 
        resume.id === updatedResume.id ? updatedResume : resume
      )
    );
  };

  const handleResumeDelete = (resumeId: string) => {
    setResumes(prev => prev.filter(resume => resume.id !== resumeId));
  };

  if (!isAuthenticated) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Authentication Required</h2>
            <p className="mt-2 text-gray-600">Please log in to manage your resumes.</p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Resume Management</h1>
              <p className="text-sm sm:text-base text-gray-600">
                Upload and manage your resumes. Our AI will analyze them to help match you with the best opportunities.
              </p>
            </div>
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 min-h-[44px] border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {showUpload ? 'Cancel Upload' : 'Upload Resume'}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section */}
        {showUpload && (
          <div className="mb-6 sm:mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Upload New Resume</h2>
              <ResumeUpload
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
              />
            </div>
          </div>
        )}

        {/* Resume List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Your Resumes</h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              {resumes.length} resume{resumes.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
          
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">Loading resumes...</span>
              </div>
            ) : (
              <ResumeVersionHistory
                resumes={resumes}
                onResumeUpdate={handleResumeUpdate}
                onResumeDelete={handleResumeDelete}
              />
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 sm:mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
          <div className="flex">
            <div className="flex-shrink-0 hidden sm:block">
              <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="sm:ml-3">
              <h3 className="text-sm font-medium text-blue-800">💡 Tips for Better Resume Analysis</h3>
              <div className="mt-2 text-xs sm:text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Upload high-quality PDF files for best results</li>
                  <li>Ensure your resume is well-formatted with clear sections</li>
                  <li>Include relevant keywords for your target positions</li>
                  <li className="hidden sm:list-item">Keep file sizes under 10MB for faster processing</li>
                  <li className="hidden sm:list-item">Processing typically takes 1-3 minutes depending on file complexity</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserDashboardLayout>
  );
}